// AML cart page additions (1.21, rebuilt in 2.23 "Cart" 2026-09-15).
//
// Four independent pieces, each guarded on its own markup being present:
//   1. Free shipping bar    ([data-aml-freeship])
//   2. Cart recommendations ([data-aml-cart-recs])
//   3. Polaris presale box  ([data-aml-presale-cart], 2.20)
//   4. "add a note"         ([data-aml-note], 2.23)
//   5. Quantity refusal     ([data-aml-qty-note], 2.24)
// Loaded with `defer` on every page; nothing here touches a page that does
// not contain the markup above.
//
// The shipping estimator that was piece 1 from 1.21 was removed in 2.23: it
// offered 239 countries for a store with Canadian rates only, defaulted to the
// wrong province, and its panel would not collapse on desktop. One plain line
// in sections/static-cart.liquid says what it used to calculate.

(function () {
  'use strict';

  const rootUrl = function () {
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root_url) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  };

  const money = function (cents) {
    const format = (window.Theme && window.Theme.moneyFormat) || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (e) {
        /* fall through to the plain format below */
      }
    }
    return '$' + (cents / 100).toFixed(2);
  };

  // -----------------------------------------------------------------
  // Cart change notifier.
  // Opal's StaticCart updates quantities over ajax: it morphdom-patches
  // [data-cart-table] and rewrites [data-total-price] innerHTML, and fires
  // no event of its own. Watch both, debounce, then re-read /cart.js once.
  // -----------------------------------------------------------------
  const cartWatchers = [];
  let cartWatchTimer = null;

  const notifyCartChanged = function () {
    if (!cartWatchers.length) return;
    if (cartWatchTimer) clearTimeout(cartWatchTimer);
    cartWatchTimer = setTimeout(function () {
      fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          cartWatchers.forEach(function (fn) {
            try { fn(cart); } catch (e) { /* one watcher must not break the others */ }
          });
        })
        .catch(function () { /* leave the last rendered state alone */ });
    }, 250);
  };

  const watchCart = function (fn) {
    cartWatchers.push(fn);
    if (cartWatchers.length > 1) return;

    const table = document.querySelector('[data-cart-table]');
    const total = document.querySelector('[data-total-price]');
    if (!table && !total) return;

    const observer = new MutationObserver(notifyCartChanged);
    if (table) observer.observe(table, { childList: true, subtree: true, characterData: true });
    if (total) observer.observe(total, { childList: true, subtree: true, characterData: true });
  };

  // -----------------------------------------------------------------
  // 0. 2.26 "Sales Paused": the checkout buttons are hidden server-side while a
  // Polaris poster is in the cart. Quantity changes and removals are ajax, so
  // reload once the cart changes and let the Liquid decide again.
  // -----------------------------------------------------------------
  const initPaused = function () {
    if (!document.querySelector('[data-aml-paused-checkout]')) return;
    watchCart(function () { window.location.reload(); });
  };

  // -----------------------------------------------------------------
  // 1. Free shipping progress bar
  // -----------------------------------------------------------------
  const initFreeShipping = function () {
    const root = document.querySelector('[data-aml-freeship]');
    if (!root) return;

    const fill = root.querySelector('[data-aml-freeship-fill]');
    const copy = root.querySelector('[data-aml-freeship-text]');
    const threshold = parseInt(root.getAttribute('data-threshold'), 10);
    if (!fill || !copy || !threshold || threshold <= 0) return;

    // [amount] rather than a Liquid-looking token: Shopify evaluates Liquid
    // inside text setting values, so a "{{ amount }}" placeholder is stripped
    // before the theme ever sees it.
    const template = root.getAttribute('data-text') || "You're [amount] away from free shipping";
    const unlocked = root.getAttribute('data-unlocked') || "You've unlocked free shipping";

    const render = function (cart) {
      const subtotal = typeof cart.items_subtotal_price === 'number'
        ? cart.items_subtotal_price
        : cart.total_price;
      const remaining = threshold - subtotal;
      const percent = Math.max(0, Math.min(100, Math.round((subtotal / threshold) * 100)));

      fill.style.width = percent + '%';
      if (remaining > 0) {
        copy.textContent = template.replace('[amount]', money(remaining));
        root.classList.remove('aml-freeship--unlocked');
      } else {
        copy.textContent = unlocked;
        root.classList.add('aml-freeship--unlocked');
      }
    };

    watchCart(render);
  };

  // -----------------------------------------------------------------
  // 2. Cart recommendations
  // -----------------------------------------------------------------
  const initRecommendations = function () {
    const root = document.querySelector('[data-aml-cart-recs]');
    if (!root) return;

    const sectionId = root.getAttribute('data-section-id');
    const limit = root.getAttribute('data-limit') || '4';
    if (!sectionId) return;

    fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          root.hidden = true;
          return null;
        }
        const productId = cart.items[0].product_id;
        const url = rootUrl() + 'recommendations/products'
          + '?section_id=' + encodeURIComponent(sectionId)
          + '&product_id=' + encodeURIComponent(productId)
          + '&limit=' + encodeURIComponent(limit);
        return fetch(url).then(function (res) { return res.text(); });
      })
      .then(function (html) {
        if (html === null || html === undefined) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = parsed.querySelector('[data-aml-cart-recs]');
        const markup = incoming ? incoming.innerHTML.trim() : '';
        if (!markup) {
          root.hidden = true;
          return;
        }
        root.innerHTML = markup;
        root.hidden = false;
      })
      .catch(function () {
        root.hidden = true;
      });
  };

  // -----------------------------------------------------------------
  // 3. Polaris presale box (2.20, 2026-09-15)
  // Liquid decides whether the box shows (snippets/aml-presale.liquid,
  // part 'cart': a product tagged polaris-2026 in the cart, and the theme
  // setting not switched off), so the rule lives in one place. This only
  // asks Shopify for that markup again, through the Section Rendering API,
  // when the cart changes without a page load:
  //   - Cart page: Opal's StaticCart patches the table over ajax (it only
  //     reloads when the cart empties); watchCart above catches it.
  //   - Opal has no cart drawer. After an add to cart it writes a message
  //     into [data-response-message] with its own "check out" button,
  //     which goes straight to checkout, so the box goes at the top of
  //     that message each time Opal writes one.
  // Any failure leaves the page as it is.
  // -----------------------------------------------------------------
  const PRESALE_SECTION = 'aml-presale-cart';

  const fetchPresaleSlot = function () {
    const url = rootUrl() + '?sections=' + PRESALE_SECTION + '&_=' + Date.now();
    return fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        const html = json && json[PRESALE_SECTION];
        if (typeof html !== 'string') return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aml-presale-cart]');
      });
  };

  const initPresale = function () {
    const slot = document.querySelector('[data-cart-table]') ? document.querySelector('.cart [data-aml-presale-cart]') : null;
    if (slot) {
      watchCart(function () {
        fetchPresaleSlot()
          .then(function (incoming) {
            if (!incoming) return;
            slot.innerHTML = incoming.innerHTML;
            slot.hidden = incoming.hasAttribute('hidden');
          })
          .catch(function () { /* keep what is showing */ });
      });
    }

    const pending = new WeakSet();
    const decorate = function (message) {
      const checkout = message.querySelector('button[name="checkout"]');
      if (!checkout || pending.has(message) || message.querySelector('[data-aml-presale-cart]')) return;
      pending.add(message);
      fetchPresaleSlot()
        .then(function (incoming) {
          pending.delete(message);
          if (!message.isConnected) return;
          // Opal rewrote the message while this was loading (another add): start again from the new one.
          if (!checkout.isConnected) { decorate(message); return; }
          if (!incoming || incoming.hasAttribute('hidden') || message.querySelector('[data-aml-presale-cart]')) return;
          message.insertBefore(incoming, message.firstChild);
        })
        .catch(function () { pending.delete(message); });
    };

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        const el = m.target && m.target.nodeType === 1 ? m.target.closest('[data-response-message]') : null;
        if (el) decorate(el);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  // -----------------------------------------------------------------
  // 4. "add a note" (2.23)
  // The textarea starts hidden behind a small link, and open when the
  // cart already carries a note.
  //
  // Saving: Opal only ever sends the note when the cart form is actually
  // submitted (the noscript "Update Cart" button, or Check Out). Every
  // quantity change and removal on this page is ajax, so a note typed and
  // left would be lost on the next page load. Nothing in the theme posts
  // it, so this does: /cart/update.js, debounced while typing and once
  // more on blur. The textarea still carries name="note", so submitting
  // the form keeps working exactly as before.
  // -----------------------------------------------------------------
  const initNote = function () {
    const root = document.querySelector('[data-aml-note]');
    if (!root) return;

    const toggle = root.querySelector('[data-aml-note-toggle]');
    const field = root.querySelector('[data-aml-note-field]');
    const textarea = root.querySelector('#cart-notes-area');
    if (!toggle || !field || !textarea) return;

    toggle.addEventListener('click', function () {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      field.hidden = open;
      if (!open) textarea.focus();
    });

    let saveTimer = null;
    let lastSaved = textarea.value;

    const save = function () {
      if (textarea.value === lastSaved) return;
      lastSaved = textarea.value;
      fetch(rootUrl() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ note: textarea.value })
      }).catch(function () {
        // Leave the typed text alone; submitting the form still sends it.
        lastSaved = null;
      });
    };

    textarea.addEventListener('input', function () {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(save, 600);
    });

    textarea.addEventListener('blur', function () {
      if (saveTimer) clearTimeout(saveTimer);
      save();
    });
  };


  // -----------------------------------------------------------------
  // 5. Quantity refusal (2.24, 2026-09-16 - audit finding 5)
  //
  // Every Polaris edition number is one physical print with stock 1, so
  // typing 2 into a quantity box is refused by Shopify with a 422 on
  // /cart/change.js. Opal's StaticCart calls window.Shopify.changeItem
  // (Shopify's own api.jquery.js), whose error path does nothing at all:
  // the box kept showing 2, the totals did not move and the shopper was
  // told nothing.
  //
  // Detection is on the response, not on the DOM: a refused change
  // produces no table mutation, so the mutation watcher above can never
  // see it. changeItem goes through XMLHttpRequest, so send() is wrapped
  // for requests to /cart/change and the status read on loadend. fetch is
  // wrapped too, in case Shopify or Opal move off jQuery later. Both
  // wrappers are installed only on a cart page that carries the message
  // markup, so no other page's networking is touched.
  // -----------------------------------------------------------------
  const initQuantityGuard = function () {
    const table = document.querySelector('[data-cart-table]');
    const note = document.querySelector('[data-aml-qty-note]');
    if (!table || !note) return;

    const isChange = function (url) {
      return typeof url === 'string' && url.indexOf('/cart/change') > -1;
    };

    const clear = function () { note.hidden = true; };

    // Put the cart's real quantities back in the boxes. Setting .value from
    // script fires no input event, so this cannot start another request.
    const refused = function () {
      note.hidden = false;
      fetch(rootUrl() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          const real = {};
          (cart.items || []).forEach(function (item) { real[item.key] = item.quantity; });
          document.querySelectorAll('[data-quantity-key]').forEach(function (input) {
            const quantity = real[input.getAttribute('data-quantity-key')];
            if (typeof quantity === 'number' && String(quantity) !== input.value) {
              input.value = String(quantity);
            }
          });
        })
        .catch(function () { /* the message still stands on its own */ });
    };

    const seen = function (status) {
      if (status === 422) refused();
      else if (status >= 200 && status < 300) clear();
    };

    const XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && !XHR.prototype.amlCartWatch) {
      const open = XHR.prototype.open;
      const send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        this.amlCartChange = isChange(url);
        return open.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        if (this.amlCartChange) {
          this.addEventListener('loadend', function () { seen(this.status); });
        }
        return send.apply(this, arguments);
      };
      XHR.prototype.amlCartWatch = true;
    }

    if (typeof window.fetch === 'function' && !window.fetch.amlCartWatch) {
      const original = window.fetch;
      const wrapped = function (input) {
        const url = typeof input === 'string' ? input : (input && input.url);
        const watching = isChange(url);
        return original.apply(this, arguments).then(function (res) {
          if (watching) seen(res.status);
          return res;
        });
      };
      wrapped.amlCartWatch = true;
      window.fetch = wrapped;
    }
  };

  const init = function () {
    initFreeShipping();
    initRecommendations();
    initPresale();
    initNote();
    initQuantityGuard();
    initPaused();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
